/**
 * Pure helpers for saving and reverting an artwork crop (JARVIS-391).
 *
 * The crop is written BESIDE the original and never over it: `url` and its
 * Storage object are untouched, so a crop is always undoable and the scanner
 * and customer apps keep the full-resolution scan. Display surfaces prefer
 * `cropUrl` through `getDisplayOriginalUrl`, and `isRenditionWritable`
 * already declines to write `_thumb.jpg` / `_med.jpg` next to a cropped
 * piece, so the crop's own thumbnail is what the fallback chain lands on.
 *
 * ROTATION IS NOT TOUCHED. One `rotation` per piece applies at display time
 * to whichever image renders (owner decision, Oct 2026): a crop made from a
 * rotated original comes out in the original's orientation frame — see
 * `perspectiveCrop.ts` — so the stored angle turns both of them, and
 * rotating a crop afterwards still works.
 *
 * No DOM or Firebase imports — unit-testable in node.
 */
import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"

/**
 * Load-bearing path marker. Revert only clears the thumbnail fields when the
 * thumbnail was minted by a crop; a `thumbnailUrl` that came from the mobile
 * upload path belongs to the original and has to survive.
 *
 * SYNC: the same marker as mobileJarvis's `deriveCropPaths` and the server's
 * `CROP_THUMBNAIL_MARKER` in `server/mobile/mobileArtworkHandlers.ts`, so
 * crops written here and crops written from the phone revert identically.
 */
export const CROP_THUMBNAIL_MARKER = "-crop-"

/**
 * Export caps, matching the mobile editor. 1600 is plenty for phone and
 * desktop display and the untouched original covers anything that ever needs
 * more; 1024 follows the existing `_thumb` convention.
 */
export const CROP_EXPORT_MAX_DIM = 1600
export const CROP_THUMB_EXPORT_MAX_DIM = 1024

export interface CropObjectPaths {
  cropPath: string
  thumbPath: string
}

/**
 * Storage object paths for a crop and its thumbnail, derived from the
 * ORIGINAL's object path so they land in the same folder.
 *
 * Timestamped rather than a stable `-crop.jpg`: clients cache by URL, so
 * overwriting a fixed name would leave stale images on screen, and every
 * upload mints a fresh download token anyway. Superseded objects orphan by
 * design, same as on mobile.
 */
export const deriveCropPaths = (
  originalPath: string,
  timestamp: number
): CropObjectPaths => {
  const slash = originalPath.lastIndexOf("/")
  const dir = slash === -1 ? "" : originalPath.slice(0, slash + 1)
  const fileName = slash === -1 ? originalPath : originalPath.slice(slash + 1)
  const dot = fileName.lastIndexOf(".")
  const base = dot > 0 ? fileName.slice(0, dot) : fileName
  return {
    cropPath: `${dir}${base}${CROP_THUMBNAIL_MARKER}${timestamp}.jpg`,
    thumbPath: `${dir}${base}${CROP_THUMBNAIL_MARKER}${timestamp}_thumb.jpg`,
  }
}

export const artworkHasCrop = (
  artwork: Pick<StudentArtwork, "cropUrl"> | undefined
): boolean => !!artwork?.cropUrl

/**
 * The image the editor opens. Always the ORIGINAL, never an existing crop,
 * so re-cropping starts from full detail instead of compounding JPEG loss
 * and progressively shaving the piece down.
 */
export const getCropEditorSourceUrl = (
  artwork: Pick<StudentArtwork, "url">
): string => artwork.url

export interface SavedCropFields {
  cropUrl: string
  cropPath: string
  thumbnailUrl: string
  thumbnailPath: string
}

/**
 * What this Quality Check session has already written for one piece.
 * `undefined` on a crop field means a revert cleared it.
 */
export type CropSessionOverride = Partial<SavedCropFields> & {
  rotation?: number
}

/**
 * A rotation-only save must keep a crop written earlier in the session.
 * Replacing the override with `{ rotation }` drops `cropUrl`, and the tile
 * falls back to the one-shot snapshot.
 */
export const mergeCropSessionOverride = (
  previous: CropSessionOverride | undefined,
  crop: SavedCropFields | null,
  rotation: number
): CropSessionOverride => ({
  ...previous,
  ...(crop ?? {}),
  rotation,
})

/**
 * Revert clears crop fields and keeps a rotation written earlier in the
 * session. That angle lives only on the override: the snapshot is one-shot
 * and does not echo it, so replacing the override would drop it from the tile
 * while Firestore still has it.
 */
export const revertCropSessionOverride = (
  previous: CropSessionOverride | undefined,
  clearedFields: CropSessionOverride
): CropSessionOverride => ({
  ...previous,
  ...clearedFields,
})

/** A Firestore update of `artwork.<id>.<field>` paths. */
export type ArtworkCropFieldUpdate = Record<string, string | number>

/**
 * Field-path update recording a saved crop, or null when the id is not in
 * the map — writing paths for a deleted piece would resurrect it as a
 * skeleton entry with a `cropUrl` and no `url`.
 *
 * Field paths rather than a whole `artwork.<id>` map write, same rule as
 * rotation and hide: `updateDoc` replaces a named field wholesale, so a map
 * write would clobber whatever another writer (a rotation flush, an
 * add-to-portfolio, an upload) put there mid-flight. Leaf paths merge
 * server-side, so the writers need no ordering between them.
 *
 * `userId` must be the audit `currentUserId`, never the effective user. It
 * rides along on the write the crop was already making, so stamping who did
 * the work costs no extra round trip.
 */
export const buildArtworkCropUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string,
  crop: SavedCropFields,
  userId: string
): ArtworkCropFieldUpdate | null => {
  if (!artworkMap?.[artworkId]) return null
  return {
    [`artwork.${artworkId}.cropUrl`]: crop.cropUrl,
    [`artwork.${artworkId}.cropPath`]: crop.cropPath,
    [`artwork.${artworkId}.thumbnailUrl`]: crop.thumbnailUrl,
    [`artwork.${artworkId}.thumbnailPath`]: crop.thumbnailPath,
    [`artwork.${artworkId}.croppedAt`]: Date.now(),
    [`artwork.${artworkId}.croppedBy`]: userId,
  }
}

/**
 * The field paths revert has to delete, or null when there is nothing to
 * undo. Returned as paths rather than a ready update so this stays pure;
 * the caller pairs each with `deleteField()`.
 *
 * The thumbnail fields only go when the stored `thumbnailPath` carries the
 * crop marker. A mobile upload arrives with its own `thumbnailUrl` for the
 * original, and clearing that would cost every surface its thumbnail.
 */
export const getArtworkCropRevertPaths = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string
): string[] | null => {
  const current = artworkMap?.[artworkId]
  if (!current?.cropUrl) return null
  const paths = [
    `artwork.${artworkId}.cropUrl`,
    `artwork.${artworkId}.cropPath`,
  ]
  if (current.thumbnailPath?.includes(CROP_THUMBNAIL_MARKER)) {
    paths.push(
      `artwork.${artworkId}.thumbnailUrl`,
      `artwork.${artworkId}.thumbnailPath`
    )
  }
  return paths
}
